/**
 * Products domain — utility functions and shared types.
 *
 * Everything in this file is non-hook: domain entity types, type
 * augmentations on top of generated shapes, imperative blob exporters,
 * URL builders, and bulk spec-sheet download helpers (which sit outside
 * the generated client because they have polymorphic responses + need
 * progress tracking for the download widget).
 */

import axios from 'axios'
import { API_HOST, clientInstance } from '../../utils/http-client'
import { exportProductsCatalog } from '../generated/clients/exportProductsCatalog'
import { exportVariantsCatalog } from '../generated/clients/exportVariantsCatalog'
import type { ExportProductsCatalogMutationRequest } from '../generated/types/ExportProductsCatalog'
import type { ExportVariantsCatalogMutationRequest } from '../generated/types/ExportVariantsCatalog'
import type { VariantCatalog as GeneratedVariantCatalog } from '../generated/types/VariantCatalog'
import type { VariantsCatalogResponse as GeneratedVariantsCatalogResponse } from '../generated/types/VariantsCatalogResponse'

// ── Domain entity type augmentations ────────────────────────────────

/**
 * Syndigo product data attached to a variant. Exposed by the backend
 * serializer behind a feature flag — drf-spectacular can't see that
 * dynamic field so it's missing from the generated `VariantCatalog`.
 */
export type SyndigoVariantProduct = {
  syndigo_product_id: string
  package_hierarchy_level?: string
  brand_name?: string | null
  category?: string | null
  package_type?: string | null
  last_modified_at_syndigo?: string | null
  last_synced_at?: string | null
}

/** Alias for legacy consumers that import `SyndigoProduct` as a type name. */
export type SyndigoProduct = SyndigoVariantProduct

/**
 * Augments the generated `VariantCatalog` with the optional
 * `syndigo_product` field that drf-spectacular doesn't see.
 */
export type VariantCatalog = GeneratedVariantCatalog & {
  syndigo_product?: SyndigoVariantProduct | null
}

/**
 * `VariantsCatalogResponse` references the augmented `VariantCatalog`
 * so consumers iterating over `data.results` see the optional
 * `syndigo_product` field.
 */
export type VariantsCatalogResponse = Omit<
  GeneratedVariantsCatalogResponse,
  'results'
> & {
  results: VariantCatalog[]
}

// ── Domain entity type re-exports / aliases ─────────────────────────

export type { ProductCatalog as Product } from '../generated/types/ProductCatalog'
export type { ProductsCatalogResponse } from '../generated/types/ProductsCatalogResponse'
export type { VariantImage } from '../generated/types/VariantImage'
export type { VariantImagesResponse } from '../generated/types/VariantImagesResponse'
export type { DistributorCode } from '../generated/types/DistributorCode'
export type { BrandList as Brand } from '../generated/types/BrandList'
export type { LabelValue } from '../generated/types/LabelValue'

// ── URL builders / imperative helpers ───────────────────────────────

/**
 * Open the Syndigo spec sheet PDF for a variant in a new tab.
 *
 * A plain `window.open` on the API URL bypasses the axios Authorization
 * header and 401s, so we fetch the PDF authenticated and load the
 * resulting blob into a tab opened up-front (preserving the user gesture
 * for popup blockers). `noopener` is intentionally omitted: it nulls out
 * the returned window reference, which we need to update the location
 * once the blob lands. Cross-origin opener risk is moot here -- the new
 * tab only ever navigates to our own `blob:` URL.
 */
export async function openSyndigoSpecsheet(variantId: number): Promise<void> {
  const placeholder = window.open('about:blank', '_blank')
  if (!placeholder) {
    throw new Error('Popup blocked')
  }
  // Paint a loading screen so the new tab isn't visibly empty while we
  // fetch the PDF.
  placeholder.document.write(
    '<!doctype html><title>Loading spec sheet…</title>' +
      '<body style="font-family:system-ui;display:flex;align-items:center;' +
      'justify-content:center;height:100vh;margin:0;color:#666">' +
      'Loading spec sheet…</body>'
  )
  placeholder.document.close()
  try {
    const response = await clientInstance.get(
      `products/catalog/variants/${variantId}/syndigo/specsheet/`,
      { params: { output: 'pdf' }, responseType: 'blob' }
    )
    const blobUrl = URL.createObjectURL(response.data as Blob)
    placeholder.location.href = blobUrl
    // Revoke after the new tab has had time to load the PDF.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
  } catch (err) {
    placeholder.close()
    throw err
  }
}

/**
 * Export the products catalog as a CSV/XLSX blob.
 *
 * Not a mutation hook — the component calls this imperatively from a
 * click handler and hands the Blob to `downloadFile`. Query params
 * (filters, sort) go via `queryParams`; the selected-columns +
 * export-type go in the body.
 */
export async function exportProductsCatalogAsBlob(
  data: ExportProductsCatalogMutationRequest,
  queryParams: Record<string, unknown> = {}
): Promise<Blob> {
  return exportProductsCatalog(data, {
    params: queryParams,
    responseType: 'blob',
  }) as unknown as Promise<Blob>
}

/**
 * Export the variants catalog as a CSV/XLSX blob.
 *
 * Same pattern as `exportProductsCatalogAsBlob`.
 */
export async function exportVariantsCatalogAsBlob(
  data: ExportVariantsCatalogMutationRequest,
  queryParams: Record<string, unknown> = {}
): Promise<Blob> {
  return exportVariantsCatalog(data, {
    params: queryParams,
    responseType: 'blob',
  }) as unknown as Promise<Blob>
}

// ── Bulk spec-sheet download helpers ────────────────────────────────
//
// These sit outside the Kubb-generated client because they:
//   - return a polymorphic response (blob for single variant, JSON
//     receipt for multi-variant async jobs) that doesn't map cleanly to
//     an OpenAPI response
//   - need `onDownloadProgress` wiring to feed the download-widget UI
//   - use `arraybuffer` to sniff between blob vs JSON at runtime

export interface BulkDownloadSpecsheetsRequest {
  include_ids: number[]
  exclude_ids?: number[]
}

export interface BulkDownloadResult {
  blob: Blob
  contentType: string
  filename: string
}

/** Response when starting an async bulk download (multiple variants) */
export interface AsyncBulkDownloadResponse {
  request_id: number
  status: 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  variant_count: number
}

/** Response when checking async bulk download status */
export interface BulkDownloadStatusResponse {
  request_id: number
  status: 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  variant_count: number
  download_url?: string
  error_message?: string
  detail?: string
  expires_at?: string
}

/** Extract filename from Content-Disposition header. */
function extractFilenameFromHeader(
  contentDisposition: string | undefined
): string | null {
  if (!contentDisposition) return null
  const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
  return match ? match[1] : null
}

function getHeader(
  headers: Record<string, unknown>,
  name: string
): string | undefined {
  const lowerName = name.toLowerCase()
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lowerName) {
      const value = headers[key]
      return typeof value === 'string' ? value : undefined
    }
  }
  return undefined
}

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'text/csv': '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/pdf': '.pdf',
  'application/zip': '.zip',
  'application/json': '.json',
  'text/plain': '.txt',
}

function buildFallbackFilename(contentType: string): string {
  const mimeType = contentType.split(';')[0].trim()
  const extension = CONTENT_TYPE_EXTENSIONS[mimeType] ?? '.zip'
  const date = new Date().toISOString().split('T')[0]
  return `download-${date}${extension}`
}

/**
 * Check the status of an async bulk download request.
 *
 * @param requestId - The request ID returned from bulkDownloadSpecsheets
 * @returns BulkDownloadStatusResponse with status and download URL if completed
 */
export async function getBulkDownloadStatus(
  requestId: number
): Promise<BulkDownloadStatusResponse> {
  const response = await clientInstance.get(
    `/products/catalog/variants/syndigo/specsheets/bulk-download/${requestId}/status/`
  )
  return response.data
}

/**
 * Download a file from the given URL.
 *
 * @param downloadUrl - The URL to download the file from
 * @param signal - AbortSignal for cancellation
 * @param onProgress - Progress callback (0-100)
 * @param defaultFilename - Default filename if not provided by server
 * @returns BulkDownloadResult containing the blob, content type, and filename
 */
export async function downloadFromUrl(
  downloadUrl: string,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void,
  defaultFilename?: string
): Promise<BulkDownloadResult> {
  // Check if URL is absolute (S3 pre-signed URLs)
  const isAbsoluteUrl =
    downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://')

  // Use plain axios for S3 URLs to avoid CORS issues from auth interceptors;
  // use clientInstance for relative URLs (our API endpoints).
  const axiosClient = isAbsoluteUrl ? axios : clientInstance

  const response = await axiosClient.get(downloadUrl, {
    ...(isAbsoluteUrl ? {} : { baseURL: API_HOST }),
    responseType: 'blob',
    signal,
    onDownloadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        )
        onProgress(progress)
      }
    },
  })

  const contentType =
    getHeader(response.headers, 'content-type') ?? 'application/zip'
  const contentDisposition = getHeader(response.headers, 'content-disposition')

  const headerFilename = extractFilenameFromHeader(contentDisposition)
  const filename =
    headerFilename || defaultFilename || buildFallbackFilename(contentType)

  return {
    blob: response.data,
    contentType,
    filename,
  }
}

/**
 * Bulk download spec sheets — sets responseType based on variant count.
 *
 * Single variant → expects PDF blob immediately.
 * Multiple variants → expects JSON response with request_id for polling.
 */
export async function bulkDownloadSpecsheetsOptimized(
  variantIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  const request: BulkDownloadSpecsheetsRequest = { include_ids: variantIds }

  // Single variant - expect blob response
  if (variantIds.length === 1) {
    const response = await clientInstance.post(
      '/products/catalog/variants/syndigo/specsheets/bulk-download/',
      request,
      {
        responseType: 'blob',
        signal,
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            )
            onProgress(progress)
          }
        },
      }
    )

    const contentType =
      getHeader(response.headers, 'content-type') ?? 'application/pdf'
    const contentDisposition = getHeader(
      response.headers,
      'content-disposition'
    )
    const defaultFilename = `specsheet_${new Date().toISOString().split('T')[0]}.pdf`

    return {
      blob: response.data,
      contentType,
      filename:
        extractFilenameFromHeader(contentDisposition) || defaultFilename,
    }
  }

  // Multiple variants - expect JSON response with request_id
  const response = await clientInstance.post(
    '/products/catalog/variants/syndigo/specsheets/bulk-download/',
    request,
    { signal }
  )

  return response.data as AsyncBulkDownloadResponse
}

/**
 * Bulk download spec sheets from deals.
 * Gets variants associated with selected deals and generates spec sheets.
 */
export async function bulkDownloadSpecsheetsFromDeals(
  dealIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  return bulkDownloadSpecsheetsAcrossDomain(
    '/contacts/deals/bulk/specsheet-download/',
    dealIds,
    'deal_specsheet',
    signal,
    onProgress
  )
}

/**
 * Bulk download spec sheets from deal reports.
 * Gets variants associated with selected deal reports and generates spec sheets.
 */
export async function bulkDownloadSpecsheetsFromDealReports(
  dealIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  return bulkDownloadSpecsheetsAcrossDomain(
    '/reports/deals/bulk/specsheet-download/',
    dealIds,
    'deal_specsheet',
    signal,
    onProgress
  )
}

/**
 * Shared implementation for deals-domain spec-sheet downloads.
 *
 * Both the contacts/deals and reports/deals endpoints return either a PDF
 * blob (single variant) or a JSON async-job receipt (multi-variant). Uses
 * `arraybuffer` to sniff the Content-Type at runtime and parse JSON when
 * appropriate, without a second request.
 */
async function bulkDownloadSpecsheetsAcrossDomain(
  endpoint: string,
  ids: number[],
  defaultFilenameStem: string,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDownloadResult | AsyncBulkDownloadResponse> {
  const request: BulkDownloadSpecsheetsRequest = { include_ids: ids }

  const response = await clientInstance.post(endpoint, request, {
    responseType: 'arraybuffer',
    signal,
    validateStatus: (status) => status === 200 || status === 202,
    onDownloadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        )
        onProgress(progress)
      }
    },
  })

  const contentType = getHeader(response.headers, 'content-type') ?? ''

  // 202 Accepted or JSON content = async response with request_id
  if (response.status === 202 || contentType.includes('application/json')) {
    const decoder = new TextDecoder('utf-8')
    const jsonStr = decoder.decode(response.data)
    return JSON.parse(jsonStr) as AsyncBulkDownloadResponse
  }

  // 200 OK with PDF/binary = blob response (single variant)
  const contentDisposition = getHeader(response.headers, 'content-disposition')
  const defaultFilename = `${defaultFilenameStem}_${new Date().toISOString().split('T')[0]}.pdf`

  return {
    blob: new Blob([response.data], { type: contentType }),
    contentType,
    filename: extractFilenameFromHeader(contentDisposition) || defaultFilename,
  }
}
